import React, { useMemo, useState, ChangeEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import classnames from 'classnames';
import {
  Typography,
  Button,
  FormItem,
  TextField,
  Datepicker,
  Dropdown,
  Tooltip,
  Icon,
} from '@getsynapse/design-system';
import {
  rangeDateType,
  NewAssignmentFormValues,
  FormOption,
} from 'utils/customTypes';
import { getOriginalProjectData } from 'state/Project/projectSlice';
import ResourceAllocationAPI from 'state/ResourceAllocation/resourceAllocationAPI';

const InlineParticipantForm: React.FC<{
  onCloseForm: () => void;
  availableRolesOptions: FormOption[];
  onAddNewParticipant: (props: NewAssignmentFormValues) => void;
  userId?: string;
}> = ({ onCloseForm, availableRolesOptions, onAddNewParticipant, userId }) => {
  const projectData = useSelector(getOriginalProjectData);
  const [availability, setAvailability] = useState<number>();

  const defaultState = useMemo(
    () => ({
      startDate: '',
      endDate: '',
      job_role: '',
      totalAllocation: 0,
    }),
    []
  );

  const [formValues, setFormValues] =
    useState<NewAssignmentFormValues>(defaultState);

  const currentAvailability = useMemo(() => {
    if (!availability) {
      return false;
    } else {
      return availability - formValues.totalAllocation;
    }
  }, [availability, formValues.totalAllocation]);

  useEffect(() => {
    const resourceAllocationAPI = ResourceAllocationAPI;

    const getUserAvailability = async () => {
      const userAvailability = await resourceAllocationAPI.getUserAvailability({
        timeFrameStartDate: formValues.startDate,
        timeFrameEndDate: formValues.endDate,
        userId: userId || '',
      });
      if (userAvailability) {
        setAvailability(userAvailability);
      } else {
        setAvailability(undefined);
      }
    };

    if (formValues.startDate && formValues.endDate && userId) {
      getUserAvailability();
    }
  }, [formValues.startDate, formValues.endDate, userId]);

  const canSave = useMemo(
    () => formValues.startDate && formValues.endDate && formValues.job_role,
    [formValues]
  );

  const handleSave = () => {
    onAddNewParticipant(formValues);
    onCloseForm();
  };

  const actionButtons = () => {
    return (
      <React.Fragment>
        <Tooltip
          timeout={0}
          position='topCenter'
          contentProps={{
            className: classnames(
              'z-10 py-1 px-3 bg-neutral-darker min-w-12',
              'text-center shadow-tooltip'
            ),
          }}
          openMode='hover2'
          trigger={
            <Button
              variant='tertiary'
              className={classnames('p-1.5', {
                'cursor-not-allowed': !canSave,
              })}
              disabled={!canSave}
              onClick={handleSave}
              data-cy='assignment-form__add-button'
            >
              <Icon
                name='add-circle'
                className={classnames('text-lg', {
                  'text-neutral-lighter': !canSave,
                  'text-neutral-black': canSave,
                })}
              />
            </Button>
          }
        >
          {intl.get('ADD')}
        </Tooltip>
        <Tooltip
          timeout={0}
          position='topCenter'
          contentProps={{
            className: classnames(
              'z-10 py-1 px-3 bg-neutral-darker min-w-12',
              'text-center shadow-tooltip'
            ),
          }}
          openMode='hover2'
          trigger={
            <Button variant='tertiary' className='p-1.5' onClick={onCloseForm}>
              <Icon name='close-circle' className='text-neutral-dark text-lg' />
            </Button>
          }
        >
          {intl.get('CANCEL')}
        </Tooltip>
      </React.Fragment>
    );
  };

  return (
    <div className='w-full flex flex-col mt-6 p-1'>
      <Typography variant='body' className='text-neutral-black'>
        {intl.get('PEOPLE.RESOURCE_ALLOCATION.TABLE.NEW_ASSIGNMENT')}
      </Typography>
      <div className='w-full flex rounded shadow-allocation-table mt-2 items-center'>
        <FormItem className='p-2 w-1/4' component='div'>
          <Dropdown
            options={availableRolesOptions}
            placeholder={intl.get(
              'PEOPLE.RESOURCE_ALLOCATION.ADD_PARTICIPANT_MODAL.PARTICIPANT_ROLE_FIELD_LABEL'
            )}
            onChange={(option: FormOption) =>
              setFormValues((prev: NewAssignmentFormValues) => ({
                ...prev,
                job_role: option.value,
              }))
            }
            triggerProps={{
              size: 'sm',
              'data-cy': 'assignment-form__role-field',
            }}
          />
        </FormItem>
        <FormItem className='p-2 w-2/6'>
          <Datepicker
            canSelectRange
            canEnterDateManually={false}
            triggerClassname='h-8 text-label'
            data-cy='assignment-form__date-range-field'
            minDate={projectData.startDate && new Date(projectData.startDate)}
            maxDate={
              projectData.targetCompletionDate &&
              new Date(projectData.targetCompletionDate)
            }
            onPickDate={(date: rangeDateType) => {
              setFormValues((prev: NewAssignmentFormValues) => ({
                ...prev,
                startDate: date.startDate,
                endDate: date.endDate,
              }));
            }}
          />
        </FormItem>
        <div className='shadow-header flex p-2 w-1/4'>
          <div className='flex flex-col items-center justify-start'>
            <Typography variant='caption' className='text-neutral-black'>
              {intl.get(
                'PEOPLE.RESOURCE_ALLOCATION.ADD_PARTICIPANT_MODAL.AVAILABLE'
              )}
            </Typography>
            {currentAvailability !== false ? (
              <Typography variant='label' className='text-neutral-black'>
                {intl.get(
                  'PEOPLE.RESOURCE_ALLOCATION.ADD_PARTICIPANT_MODAL.HOURS_AVAILABLE',
                  { hours: currentAvailability }
                )}
              </Typography>
            ) : (
              <span>-</span>
            )}
          </div>
          <div className='flex items-center ml-2'>
            <TextField
              placeholder={intl.get(
                'PEOPLE.RESOURCE_ALLOCATION.ADD_PARTICIPANT_MODAL.TOTAL_ALLOCATION_PLACEHOLDER'
              )}
              height='small'
              type='number'
              inputClassName='no-spinner'
              data-cy='assignment-form__allocation-field'
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFormValues((prev: NewAssignmentFormValues) => ({
                  ...prev,
                  totalAllocation: parseFloat(e.target.value) || 0,
                }))
              }
            />
            <Typography variant='label' className='text-neutral-black ml-2'>
              {intl.get(
                'PEOPLE.RESOURCE_ALLOCATION.UPDATE_WEEKLY_ALLOCATION_MODAL.HOUR_LABEL'
              )}
            </Typography>
          </div>
        </div>
        <div className='flex p-2 items-center align-middle'>
          {actionButtons()}
        </div>
      </div>
    </div>
  );
};

export default InlineParticipantForm;
